import { useState, useEffect } from "react"
import {
  useLazyFetchLeadDetailsQuery,
  useLazyFetchSubscriptionDetailsQuery
} from "../../../../../../redux/queries"

interface UseTemplatePreviewProps {
  selectedRecordId: string
  dataType: string
  template?: string
  fieldMapping: any
}

export const useTemplatePreview = ({
  selectedRecordId,
  dataType,
  template,
  fieldMapping
}: UseTemplatePreviewProps) => {
  const [mappedPreview, setMappedPreview] = useState<any>(null)
  const [showPreview, setShowPreview] = useState(false)

  const [fetchLeadDetails, { isLoading: isFetchingLead }] =
    useLazyFetchLeadDetailsQuery()
  const [fetchSubscriptionDetails, { isLoading: isFetchingSubscription }] =
    useLazyFetchSubscriptionDetailsQuery()

  const isFetchingRecord = isFetchingLead || isFetchingSubscription

  const applyTemplateVariables = (template: string, record: any): any => {
    if (!template) return null

    try {
      let processed = template

      // Replace item.* variables
      processed = processed.replace(/\{\{item\.email\}\}/g, record.email || "")
      processed = processed.replace(/\{\{item\.phone\}\}/g, record.phone || "")
      processed = processed.replace(
        /\{\{item\.details\.first_name\}\}/g,
        record.details?.first_name || ""
      )
      processed = processed.replace(
        /\{\{item\.details\.last_name\}\}/g,
        record.details?.last_name || ""
      )
      processed = processed.replace(
        /\{\{item\.details\.loan_amount\}\}/g,
        record.details?.loan_amount || 0
      )
      processed = processed.replace(
        /\{\{item\.details\.credit_score\}\}/g,
        record.details?.credit_score || 0
      )
      processed = processed.replace(
        /\{\{item\.details\.loan_purpose\}\}/g,
        record.details?.loan_purpose || ""
      )
      processed = processed.replace(
        /\{\{item\.details\.residency_type\}\}/g,
        record.details?.residency_type || ""
      )
      processed = processed.replace(
        /\{\{item\.details\.property_type\}\}/g,
        record.details?.property_type || ""
      )
      processed = processed.replace(
        /\{\{item\.details\.loan_type\}\}/g,
        record.details?.loan_type || ""
      )

      // Replace lo.* variables (mock data for preview)
      processed = processed.replace(
        /\{\{lo\.full_name\}\}/g,
        "John Loan Officer"
      )
      processed = processed.replace(/\{\{lo\.nmls\}\}/g, "123456")
      processed = processed.replace(/\{\{lo\.email\}\}/g, "lo@example.com")
      processed = processed.replace(/\{\{lo\.phone\}\}/g, "555-1234")

      // Replace brand.* variables (mock data for preview)
      processed = processed.replace(/\{\{brand\.title\}\}/g, "Sample Bank")
      processed = processed.replace(/\{\{brand\.nmls\}\}/g, "654321")

      return JSON.parse(processed)
    } catch (error) {
      return { error: "Failed to process template" }
    }
  }

  const generatePreview = async () => {
    if (!selectedRecordId) {
      setMappedPreview(null)
      setShowPreview(false)
      return
    }

    try {
      let record
      if (dataType === "lead") {
        record = await fetchLeadDetails(selectedRecordId).unwrap()
      } else if (
        dataType === "subscription" ||
        dataType === "pending_subscription"
      ) {
        record = await fetchSubscriptionDetails({
          id: selectedRecordId
        }).unwrap()
      }

      let preview
      if (template) {
        preview = applyTemplateVariables(template, record)
      } else {
        preview = {
          note: "Using field mapping (no template defined)",
          field_mapping: fieldMapping,
          sample_record: record
        }
      }

      setMappedPreview(preview)
      setShowPreview(true)
    } catch (error) {
      setMappedPreview({ error: "Failed to generate preview" })
      setShowPreview(true)
    }
  }

  useEffect(() => {
    if (selectedRecordId) {
      generatePreview()
    } else {
      setShowPreview(false)
      setMappedPreview(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecordId])

  return {
    mappedPreview,
    showPreview,
    isFetchingRecord,
    generatePreview
  }
}
